// Iter152 · Helpers y constantes compartidas del split de OfertasComercial.
// Extraído de /app/frontend/src/pages/OfertasComercial.jsx sin cambios de lógica.

export const fmtEur = (n) =>
  n == null
    ? "—"
    : new Intl.NumberFormat("es-ES", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 2,
      }).format(n);
export const fmtMM = (n) => (n == null ? "—" : `${Number(n).toFixed(2)} M€`);
export const fmtPct = (n) => (n == null ? "—" : `${(n * 100).toFixed(1)}%`);

// Iter307 · G06 — las fechas comerciales se guardan en ISO (ej. "2026-09-28T14:00:00")
// pero nunca deben mostrarse así en pantalla; se formatean a DD/MM/AAAA (es-ES).
export function fmtFechaOferta(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export const ESTADO_CLS = {
  "BORRADOR":        "bg-orange-100 text-orange-700 border border-orange-300",
  "PRESENTADA":      "bg-blue-100 text-blue-700",
  "NO PRESENTADA":   "bg-rose-100 text-rose-700",
  "POR DECIDIR":     "bg-amber-100 text-amber-700",
  "ESTUDIO":         "bg-indigo-100 text-indigo-700",
  "PUBLICADA":       "bg-gray-100 text-gray-700",
};
export const FASE_CLS = {
  "ADJUDICADA":       "bg-green-100 text-green-700",
  "FIRMADA":          "bg-green-200 text-green-800",
  "ADJUDICADA_OTROS": "bg-rose-100 text-rose-700",
  "NO_ADJUDICADA":    "bg-rose-100 text-rose-700",
  "PTE_APERTURA":     "bg-amber-100 text-amber-700",
  "CLASIFICADOS_1":   "bg-blue-100 text-blue-700",
};

// Definición de TODAS las columnas posibles. `key` casa con campos del doc.
export const ALL_COLS = [
  { key: "objeto",            label: "Concurso / Expediente", default: true,  width: "w-[280px]" },
  { key: "origen",            label: "Origen",                default: true,  width: "w-[60px]",  align: "center" },
  { key: "cliente",           label: "Cliente",               default: true,  width: "w-[180px]" },
  { key: "actividad",         label: "Actividad",             default: true,  width: "w-[140px]" },
  { key: "tipo_contratacion", label: "Tipo Contr.",           default: true,  width: "w-[90px]",  align: "center" },
  { key: "zona",              label: "Zona",                  default: true,  width: "w-[100px]" },
  { key: "gerencia",          label: "Gerencia",              default: false, width: "w-[110px]" },
  { key: "ute",               label: "UTE",                   default: true,  width: "w-[90px]",  align: "center" },
  { key: "irr",               label: "IRR",                   default: true,  width: "w-[80px]",  align: "right" },
  { key: "importe_ute",       label: "Importe total (base)", default: false, width: "w-[130px]", align: "right" },
  { key: "duracion",          label: "Duración (m)",          default: true,  width: "w-[80px]",  align: "right" },
  { key: "prorroga_col",      label: "Prórroga (m)",          default: false, width: "w-[95px]",  align: "right" },
  { key: "mm",                label: "M€ con UTE",            default: true,  width: "w-[80px]",  align: "right", title: "Importe correspondiente aplicando % en UTE (en millones)" },
  { key: "fecha_presentacion",label: "F. Presentación",       default: true,  width: "w-[110px]" },
  { key: "fecha_apertura",    label: "F. Apertura",           default: false, width: "w-[110px]" },
  { key: "fecha_adjudicacion",label: "F. Adjudicación",       default: false, width: "w-[110px]" },
  { key: "estado",            label: "Estado",                default: true,  width: "w-[110px]" },
  { key: "fase_adj",          label: "Fase adj.",             default: true,  width: "w-[140px]" },
  { key: "responsable",       label: "Responsable",           default: true, width: "w-[120px]" },
  { key: "ai_score",          label: "Score Pando",              default: false, width: "w-[70px]",  align: "center" },
];

// Compute IRR (Internal Rate of Return) approx if not provided explicitly.
// Uses: ((EBITDA · años) / CAPEX − 1) / años · 100   — simple unlevered approximation.
// Returns { value, auto } where auto=true if computed (no manual value).
export function computeIRR(o) {
  const manual = (o.ue_irr_unlevered != null && o.ue_irr_unlevered !== "")
    ? Number(o.ue_irr_unlevered) : null;
  if (manual != null && !Number.isNaN(manual)) return { value: manual, auto: false };

  const capex = Number(o.ue_capex);
  const ebitda = Number(o.ue_ebitda);
  const anios = Number(o.duracion_anios);
  if (capex > 0 && ebitda > 0 && anios > 0) {
    const irr = ((ebitda * anios) / capex - 1) / anios * 100;
    if (Number.isFinite(irr)) return { value: irr, auto: true };
  }
  // Fallback rough: gross_margin / 4 (heurística muy aproximada)
  const gm = Number(o.ue_gross_margin);
  if (gm > 0 && Number.isFinite(gm)) {
    return { value: gm / 4, auto: true };
  }
  return { value: null, auto: false };
}

// Iter152 · Riesgo UTE. Devuelve `low` (<30% VSM: riesgo control por socio) o
// `high` (>70% VSM: dependencia excesiva). Sólo se calcula cuando
// `porcentaje_ute` está definido y >0, independientemente del checkbox
// (la misma condición que activa el badge UTE por evidencia).
export function computeUteRisk(o) {
  const pct = Number(o.porcentaje_ute);
  if (!(pct > 0)) return null;
  if (pct < 30) return "low";
  if (pct > 70) return "high";
  return null;
}

// Iter180 · Detección UTE unificada. Consistente entre la lista y la ficha.
// Una oferta se considera UTE si:
//   - el checkbox `ute` está marcado, o
//   - `porcentaje_ute > 0` y < 100 (VSM no es el único socio), o
//   - `empresa_oferta` contiene un separador ("/" o " - ") con más de una empresa.
export function isUte(o) {
  if (!o) return false;
  const pct = Number(o.porcentaje_ute) || 0;
  const empresas = parseUteEmpresas(o);
  if (o.ute) return true;
  if (pct > 0 && pct < 100) return true;
  if (empresas.length > 1) return true;
  return false;
}

// Iter180 · Divide `empresa_oferta` en la lista de socios que forman la UTE.
// Aceptamos separadores "/" o " - " (con espacios). Filtra tokens vacíos.
export function parseUteEmpresas(o) {
  const raw = String(o?.empresa_oferta || "").trim();
  if (!raw) return [];
  // Split por " - " (con espacios) para no romper nombres como "VSM-Sur"
  const parts = raw.split(/\s+-\s+|\s*\/\s*/).map(s => s.trim()).filter(Boolean);
  return parts;
}

// Iter180 · Devuelve una etiqueta legible con socios y porcentajes para
// mostrar en listas y en la ficha. Formatos:
//   - "VSM 60% · ACCIONA 40%"  (cuando conocemos ambos socios)
//   - "VSM 60% · Socio 40%"    (cuando solo conocemos VSM y su %)
//   - "VSM/ACCIONA"            (cuando no hay %)
//   - "UTE"                    (fallback sin datos)
export function formatUteLabel(o) {
  const pctVsm = Number(o?.porcentaje_ute) || 0;
  const empresas = parseUteEmpresas(o);
  const vsmName = (o?.empresa_g_valoriza || "VSM").toString().trim() || "VSM";
  // Sacamos el nombre del socio no-VSM (si lo hay)
  const socios = empresas.filter(n => n.toUpperCase() !== vsmName.toUpperCase());
  if (pctVsm > 0 && pctVsm <= 100) {
    const pctSocio = Math.max(0, +(100 - pctVsm).toFixed(2));
    if (socios.length === 0) {
      // Sabemos % VSM pero no el nombre del socio
      return pctSocio > 0
        ? `${vsmName} ${pctVsm}% · Socio ${pctSocio}%`
        : `${vsmName} ${pctVsm}%`;
    }
    if (socios.length === 1) {
      return pctSocio > 0
        ? `${vsmName} ${pctVsm}% · ${socios[0]} ${pctSocio}%`
        : `${vsmName} ${pctVsm}% · ${socios[0]}`;
    }
    // Varios socios: no repartimos el % restante entre ellos
    return `${vsmName} ${pctVsm}% · ${socios.join(" · ")}`;
  }
  // Sin %: mostrar la lista tal cual
  if (empresas.length > 1) return empresas.join(" · ");
  if (socios.length === 1) return `${vsmName} · ${socios[0]}`;
  return "UTE";
}

// Columnas relevantes por estado.
export const COLS_POR_ESTADO = {
  "BORRADOR":      ["objeto", "origen", "cliente", "actividad", "mm", "fecha_presentacion", "estado", "ai_score"],
  "POR DECIDIR":   ["objeto", "origen", "cliente", "actividad", "tipo_contratacion", "zona", "ute", "mm", "fecha_presentacion", "estado"],
  "ESTUDIO":       ["objeto", "origen", "cliente", "actividad", "tipo_contratacion", "zona", "ute", "irr", "mm", "fecha_presentacion", "estado", "responsable"],
  "PUBLICADA":     ["objeto", "origen", "cliente", "actividad", "tipo_contratacion", "zona", "ute", "irr", "mm", "fecha_presentacion", "estado", "responsable"],
  "PRESENTADA":    ["objeto", "origen", "cliente", "actividad", "tipo_contratacion", "zona", "ute", "irr", "mm", "fecha_presentacion", "fecha_apertura", "estado", "fase_adj", "responsable"],
  "NO PRESENTADA": ["objeto", "origen", "cliente", "actividad", "tipo_contratacion", "zona", "ute", "mm", "fecha_presentacion", "estado"],
};

// Vistas predefinidas.
// Iter181 · Orden y estados alineados con la petición del negocio.
// Iter199 · Publicadas / Sin decidir / En estudio se GESTIONAN sólo desde
// Mercado (Iter192). En Licitaciones se muestran únicamente los estados
// del pipeline VIVO (Todas + Presentadas y fases de adjudicación).
// Iter201 · Añadidos tabs de estados terminales post-presentación (SUSPENDIDA,
// ANULADA, DESISTIDA). Estos son los casos "post": suspensión de pliego durante
// la apertura, anulación tras impugnación, o desistimiento tras adjudicar.
export const VISTAS_PREDEFINIDAS = [
  { key: "all",             label: "Todas",             estado: "",              faseAdj: "" },
  { key: "presentadas",     label: "Presentadas",       estado: "PRESENTADA",    faseAdj: "" },
  { key: "apertura",        label: "Pte. Apertura",     estado: "PRESENTADA",    faseAdj: "PTE_APERTURA" },
  { key: "clasif1",         label: "Clasificados 1",    estado: "PRESENTADA",    faseAdj: "CLASIFICADOS_1" },
  { key: "pte_adj_otros",   label: "Pte. Adj. otros",   estado: "PRESENTADA",    faseAdj: "PTE_ADJUDICAR_OTROS" },
  { key: "adjudicadas",     label: "Adjudicadas",       estado: "PRESENTADA",    faseAdj: "ADJUDICADA" },
  { key: "adj_otros",       label: "Adj. otros",        estado: "PRESENTADA",    faseAdj: "ADJUDICADA_OTROS" },
  { key: "no_adjudicado",   label: "No adjudicado",     estado: "PRESENTADA",    faseAdj: "NO_ADJUDICADA" },
  { key: "firmadas",        label: "Firmadas",          estado: "PRESENTADA",    faseAdj: "FIRMADA" },
  { key: "suspendida",      label: "Suspendida",        estado: "SUSPENDIDA",    faseAdj: "" },
  { key: "anulada",         label: "Anulada (post)",    estado: "ANULADA",       faseAdj: "" },
  { key: "desistida",       label: "Desistida (post)",  estado: "DESISTIDA",     faseAdj: "" },
];

// Iter166/171 · Licitaciones muestra ofertas en cualquier estado. El
// dropdown incluye todos los estados válidos + opción "Todos". El toggle
// "Solo activas 2026+vivas" hace de filtro complementario para acotar.
// Iter201 · Estados posibles en el filtro de Licitaciones.
// NO incluye PUBLICADA / POR DECIDIR / ESTUDIO / BORRADOR / NO PRESENTADA
// porque esas viven en Mercado. En Licitaciones sólo aparecen las ofertas
// PRESENTADAS y las que TRAS presentarse acabaron en SUSPENDIDA/ANULADA/DESISTIDA.
export const ESTADOS_OPCIONES = ["PRESENTADA", "SUSPENDIDA", "ANULADA", "DESISTIDA"];
// Roles que pueden borrar/restaurar licitaciones (debe coincidir con el backend
// core.auth.ADMIN_ROLES / require_admin_or_manager). Centralizado para no duplicar.
export const CAN_DELETE_ROLES = ["admin", "it_validator", "offer_manager", "cfo", "director", "controller", "responsable_operativo"];
export const FASES_ADJ = [
  "PTE_APERTURA", "CLASIFICADOS_1", "CLASIFICADOS_2", "PTE_ADJUDICAR",
  "ADJUDICADA", "NO_ADJUDICADA", "FIRMADA", "RECURSO",
];

// Cascada DG → actividades — fuente única de verdad (replicada en backend)
// R08 (auditoría externa 09/10/2026) · O&M/INDUSTRIAL (TRATAMIENTO) y
// "Facility Services" (SERVICIOS) existen en datos reales pero no eran
// filtrables ni seleccionables — añadidos para que coincida con la
// realidad de los datos (backend/routers/ofertas_comercial.py CATALOGS).
export const ACTIVIDADES_POR_DG = {
  "SERVICIOS":   ["C&W", "GA", "MOB", "Facility Services"],
  "TRATAMIENTO": ["Light Capex", "PPP", "O&M", "INDUSTRIAL"],
};
export const ALL_ACTIVIDADES = Object.values(ACTIVIDADES_POR_DG).flat();

export const MOTIVOS_NO_PRESENTACION = [
  "Profitability",
  "No Chance",
  "Workload (Resources)",
  "Non-strategic",
  "Solvency",
];


// Mapa zona organizativa → gerencias (derivado de los datos reales:
// cada gerencia pertenece a una única zona). Se usa para acotar el
// desplegable de Gerencia cuando el usuario ya ha elegido una zona.
export const ZONA_GERENCIAS = {
  ZONA_1: [
    "Asturias, Cantabria, Castilla-León", "Cataluña", "Comunidad Madrid - CAM I",
    "Comunidad Madrid - CAM II", "Consell Comarcal del Maresme", "Galicia",
    "País Vasco, La Rioja, Navarra", "Residuos Zona Norte",
  ],
  ZONA_2: [
    "Aeropuerto de Tenerife", "Andalucía Occidental",
    "Andalucía Oriental, Extremadura, Ceuta y Melilla", "Andalucía y Extremadura",
    "Aragón", "Canarias", "Castilla_La Mancha", "Residuos Zona Centro",
    "Valencia, Baleares, Murcia", "Valencia, Balearares, Murcia",
  ],
  ZONA_3: ["Servicios Madrid", "Tratamiento", "Zonas Verdes_Madrid"],
};
